// Apps published to a project, served as MCP apps by the analysis-agent's
// project apps server and rendered in the session pane like any MCP app.
import { apiMutate } from "~/utils/apiClient";
import { fetchMcpAppResource, type FetchedMcpAppResource } from "./fetchMcpAppResource";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

export const PROJECT_APPS_SERVER_ID = "project_apps";

// Which app a panel shows and the session it is open in; a draft's is the session
// holding it. A newer `as_of` for the same app tells its runtime to read state again.
export interface ProjectAppInstance {
  project_id: string;
  slug: string;
  title: string;
  conversation_id: string | null;
  draft: boolean;
  template_id: string;
  as_of: string;
}

export interface ProjectAppEnvelope {
  project_app: ProjectAppInstance;
}

export interface ProjectAppRef {
  projectId: string;
  slug: string;
}

// Router state asking a session to open a published app full screen as it is entered.
export interface OpenProjectAppState {
  openProjectApp: ProjectAppRef;
}

// `draftIn` names the session holding a draft of the app.
export function projectAppResourceUri(projectId: string, slug: string, draftIn?: string): string {
  return draftIn
    ? `ui://project-apps/${projectId}/drafts/${draftIn}/${slug}`
    : `ui://project-apps/${projectId}/${slug}`;
}

export function isProjectAppEnvelope(value: unknown): value is ProjectAppEnvelope {
  if (typeof value !== "object" || value === null || !("project_app" in value)) {
    return false;
  }
  const instance = value.project_app;
  return (
    typeof instance === "object" &&
    instance !== null &&
    "project_id" in instance &&
    typeof instance.project_id === "string" &&
    "slug" in instance &&
    typeof instance.slug === "string" &&
    "as_of" in instance &&
    typeof instance.as_of === "string"
  );
}

// The tools an app's runtime calls through its host. The agent's tools, such as
// `open_<slug>`, are not among them, so a panel can't reach other apps through them.
const PANEL_TOOLS: ReadonlySet<string> = new Set([
  "load_app",
  "read_input",
  "read_state",
  "write_state",
  "call_app_tool",
]);
const SESSION_TOOLS: ReadonlySet<string> = new Set(["load_app", "call_app_tool"]);

/**
 * A panel's tool call, with its project, app and session taken from the envelope the
 * host opened it with rather than from the panel, so an app's code can only act as itself.
 */
export function bindPanelToolCall(
  envelope: unknown,
  tool: string,
  args: Record<string, unknown>
): Record<string, unknown> {
  if (!PANEL_TOOLS.has(tool)) {
    throw new Error(`An app can't call "${tool}"`);
  }
  if (!isProjectAppEnvelope(envelope)) {
    throw new Error("The app has not loaded yet");
  }
  const { project_id, slug, conversation_id, draft } = envelope.project_app;
  if (tool === "call_app_tool" && !conversation_id) {
    throw new Error("Tools run in a session: open this app with a copilot to use them");
  }
  const bound: Record<string, unknown> = { ...args, project_id, slug };
  delete bound.conversation_id;
  delete bound.draft;
  // A draft is found by its session, so every one of its calls names it.
  if (draft) {
    bound.draft = true;
  }
  if ((draft || SESSION_TOOLS.has(tool)) && conversation_id) {
    bound.conversation_id = conversation_id;
  }
  return bound;
}

/** An app's panel document, from the project apps server through the resource proxy. */
export function fetchProjectAppPanel(
  projectId: string,
  slug: string,
  draftIn?: string,
  signal?: AbortSignal
): Promise<FetchedMcpAppResource> {
  const params = new URLSearchParams({
    server_id: PROJECT_APPS_SERVER_ID,
    uri: projectAppResourceUri(projectId, slug, draftIn),
  });
  return fetchMcpAppResource(`${VITE_ANALYSIS_AGENT_API}/mcp-resources/fetch?${params}`, {
    signal,
  });
}

/**
 * The envelope a host pushes to an app's panel when it opens the app itself. A
 * `draft` is the latest template `slug` in the session `conversationId`.
 */
export async function loadProjectApp(
  projectId: string,
  slug: string,
  {
    conversationId,
    draft = false,
    signal,
  }: { conversationId?: string; draft?: boolean; signal?: AbortSignal } = {}
): Promise<ProjectAppEnvelope> {
  const result = await apiMutate<unknown>(
    `${VITE_ANALYSIS_AGENT_API}/mcp-resources/call`,
    "POST",
    {
      server_id: PROJECT_APPS_SERVER_ID,
      tool: "load_app",
      arguments: {
        project_id: projectId,
        slug,
        ...(conversationId ? { conversation_id: conversationId } : {}),
        ...(draft ? { draft } : {}),
      },
    },
    { signal }
  );
  if (!isProjectAppEnvelope(result)) {
    throw new Error(`Opening app "${slug}" returned no app`);
  }
  return result;
}

export function openProjectAppOnEntry(state: unknown): ProjectAppRef | undefined {
  if (typeof state !== "object" || state === null || !("openProjectApp" in state)) {
    return undefined;
  }
  const request = state.openProjectApp;
  return typeof request === "object" &&
    request !== null &&
    "projectId" in request &&
    typeof request.projectId === "string" &&
    "slug" in request &&
    typeof request.slug === "string"
    ? { projectId: request.projectId, slug: request.slug }
    : undefined;
}
